/* eslint-disable import/no-cycle */
// External
import { lazy } from 'react';

// Internal
import { allExceptInvoiceOnly } from '../../util/rolePermissions';
import accountRoutes from './accountRoutes';
import orderRoutes from './orderRoutes';
import shopRoutes from './shopRoutes';

const Account = lazy(() => import('../../account/Account'));
const Cart = lazy(() => import('../../carts/Cart/Cart'));
const Checkout = lazy(() => import('../../carts/checkout/Checkout'));
const ConfirmMigration = lazy(() => import('../../migrate/ConfirmMigration'));
const ContactUs = lazy(() => import('../../user/homePage/ContactUs'));
const LanguagePicker = lazy(() => import('../../static/languagePicker/LanguagePicker'));
const LegacyPasswordRecover = lazy(() => import('../../migrate/recovery/LegacyPasswordRecover'));
const LegacyPasswordSent = lazy(() => import('../../migrate/recovery/LegacyPasswordSent'));
const LegacyUsernameRecovery = lazy(() => import('../../migrate/recovery/LegacyUsernameRecovery'));
const LegacyUsernameSent = lazy(() => import('../../migrate/recovery/LegacyUsernameSent'));
const LoginPage = lazy(() => import('../../auth/LoginPage'));
const NewPasswordRecover = lazy(() => import('../../user/passwordReset/NewPasswordRecover'));
const NewPasswordSent = lazy(() => import('../../user/passwordReset/NewPasswordSent'));
const MigrateFlow = lazy(() => import('../../migrate/MigrateFlow'));
const MigrateSplash = lazy(() => import('../../migrate/MigrateSplash'));
const Orders = lazy(() => import('../../orders/Orders'));
const OrderConfirmation = lazy(() => import('../../carts/checkout/OrderConfirmation.jsx'));
const Outage = lazy(() => import('../../outages/Outage'));
const Profile = lazy(() => import('../../user/Profile'));
const RegistrationFlow = lazy(() => import('../../migrate/RegistrationFlow'));
const Shop = lazy(() => import('../../shop'));
const SelectAccount = lazy(() => import('../../user/SelectAccount'));
const TermsConditions = lazy(() => import('../../static/termsConditions/TermsConditions'));
const VerifyEmail = lazy(() => import('../../migrate/VerifyEmail'));

export const routes = [
  // Navigation Slider routes
  {
    path: '/shop',
    title: 'global.shop',
    component: Shop,
    section: 'global.shop',
    nav: true,
    exact: true,
    routes: shopRoutes,
    limitToRoles: allExceptInvoiceOnly
  },
  {
    path: '/account',
    title: 'route.account.title',
    component: Account,
    section: 'route.account.title',
    nav: true,
    exact: false,
    routes: accountRoutes,
    hideLink: true
  },
  {
    path: '/orders',
    title: 'global.orders',
    component: Orders,
    section: 'global.orders',
    nav: true,
    exact: false,
    routes: orderRoutes,
    hideLink: true
  },
  {
    path: '/profile',
    title: 'global.profile',
    component: Profile,
    section: 'global.profile',
    nav: false,
    exact: true
  },
  {
    path: '/cart',
    title: 'global.cart',
    component: Cart,
    section: 'global.cart',
    nav: false,
    exact: true,
    isShopping: true
  },
  {
    path: '/checkout',
    title: 'route.checkout.title',
    component: Checkout,
    section: 'route.checkout.title',
    nav: false,
    exact: true
  },
  {
    path: '/review-order',
    title: 'route.checkout.review.title',
    component: Checkout,
    section: 'route.checkout.review.title',
    nav: false,
    exact: true
  },
  {
    path: '/order-confirmation',
    title: 'route.checkout.confirmation.title',
    component: OrderConfirmation,
    section: 'route.checkout.confirmation.title',
    nav: false,
    exact: true
  },
  // App routes
  {
    path: '/login',
    component: LoginPage,
    nav: false,
    insecure: true
  },
  {
    path: '/select-accounts',
    component: SelectAccount,
    nav: false,
    insecure: false
  },
  {
    path: '/contact-us',
    component: ContactUs,
    nav: false,
    insecure: false
  },
  {
    path: '/migrate/welcome',
    component: LoginPage,
    nav: false,
    insecure: true
  },
  {
    path: '/migrate/internal/welcome',
    component: LoginPage,
    nav: false,
    insecure: true
  },
  {
    path: '/invite/welcome',
    component: LoginPage,
    nav: false,
    insecure: true
  },
  {
    path: '/migrate',
    component: MigrateSplash,
    nav: false,
    insecure: true
  },
  {
    path: '/migrate-steps',
    component: MigrateFlow,
    nav: false,
    insecure: true
  },
  {
    path: '/invite/accept',
    component: RegistrationFlow,
    nav: false,
    insecure: true
  },
  {
    path: '/confirm-migrate',
    component: ConfirmMigration,
    nav: false,
    insecure: true
  },
  {
    path: '/verify-email',
    component: VerifyEmail,
    nav: false,
    insecure: true
  },
  {
    path: '/legacy/username/recover',
    component: LegacyUsernameRecovery,
    nav: false,
    insecure: true
  },
  {
    path: '/legacy/username/sent',
    component: LegacyUsernameSent,
    nav: false,
    insecure: true
  },
  {
    path: '/password/recover',
    component: props => <NewPasswordRecover type='recover' {...props} />,
    nav: false,
    insecure: true
  },
  {
    path: '/password/sent',
    component: props => <NewPasswordSent type='recover' {...props} />,
    nav: false,
    insecure: true
  },
  {
    path: '/password/reset',
    component: props => <NewPasswordRecover type='reset' {...props} />,
    nav: false,
    insecure: true
  },
  {
    path: '/password/reset-sent',
    component: props => <NewPasswordSent type='reset' {...props} />,
    nav: false,
    insecure: true
  },
  {
    path: '/legacy/password/recover',
    component: props => <LegacyPasswordRecover type='recover' {...props} />,
    nav: false,
    insecure: true
  },
  {
    path: '/legacy/password/sent',
    component: props => <LegacyPasswordSent type='recover' {...props} />,
    nav: false,
    insecure: true
  },
  {
    path: '/legacy/password/reset',
    component: props => <LegacyPasswordRecover type='reset' {...props} />,
    nav: false,
    insecure: true
  },
  {
    path: '/legacy/password/reset-sent',
    component: props => <LegacyPasswordSent type='reset' {...props} />,
    nav: false,
    insecure: true
  },
  {
    path: '/outage',
    component: Outage,
    nav: false,
    insecure: true
  },
  {
    path: '/static/terms-conditions',
    component: TermsConditions,
    nav: false,
    insecure: true
  },
  {
    path: '/static/language-select',
    component: LanguagePicker,
    nav: false,
    insecure: true
  }
];

export const navRoutes = routes.filter(r => r.nav);
